import { FC, useMemo } from 'react';
import { Field, useForm } from 'react-final-form';
import { useQuery } from '@tanstack/react-query';

import {
    Box,
    Button,
    Loading,
    SectionMessage,
    SelectFinalField,
    TextFinalField,
} from '@thirdspacelearning/library';
import {
    QUERY_STATUS,
    required,
    requiredArray,
} from '@tsl-frontend/tsl-utilities';

import { LearningContentsAttributes } from './sections';

import { getYears } from '@api';
import { CurriculumQueryKeys } from '@services';
import { Country, LearningObjective } from '@types';
import { countryOptions } from '@utils';
import {
    AutocompleteFinalField,
    CheckboxFinalField,
    Condition,
    DeleteConfirmationDialog,
} from '@shared';

const sx = {
    activateButton: {
        marginRight: 1,
    },
};

interface Props {
    push: (...args: any[]) => any;
    learningObjective?: LearningObjective;
    dirtySinceLastSubmit: boolean;
    editLO: (data: { id: number; data: any }) => void;
    deleteLO: (id: number) => void;
}

const LearningObjectiveFormContent: FC<Props> = ({
    push,
    learningObjective,
    editLO,
    deleteLO,
}) => {
    const form = useForm();

    const { status, data: years } = useQuery({
        queryKey: [CurriculumQueryKeys.YEARS],
        queryFn: () => getYears(),
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });

    const changeActive = async () => {
        if (learningObjective) {
            editLO({
                id: learningObjective.id,
                data: { active: !learningObjective.active },
            });
        }
    };

    const handleDeleteLO = async () => {
        if (learningObjective) {
            deleteLO(learningObjective.id);
        }
    };

    const yearsOptions = useMemo(
        () =>
            years &&
            years.map((year) => ({
                id: `${year.id}`,
                name: year.name,
            })),
        [years],
    );

    const gradeOptions = useMemo(
        () =>
            years &&
            years
                .filter((year) => ![1, 2, 9, 10, 11].includes(year.id))
                .map((year) => ({
                    id: `${year.id}`,
                    name: `Grade ${year.id}`,
                })),
        [years],
    );

    return (
        <>
            {status === QUERY_STATUS.pending && <Loading />}

            {status === QUERY_STATUS.error && (
                <SectionMessage
                    type="error"
                    title="Error getting year groups"
                />
            )}

            {status === QUERY_STATUS.success && (
                <>
                    <Box
                        mt={3}
                        display="flex"
                        flexDirection="column"
                        data-testid="learning-objective-form"
                    >
                        <Field
                            name="country"
                            component={SelectFinalField}
                            label="Region*"
                            options={countryOptions}
                            variant="outlined"
                            validate={required}
                            onChangeCallback={() => {
                                form.change('year_id', undefined);
                                form.resetFieldState('year_id');
                            }}
                            fullWidth
                            required
                        />

                        <Field
                            name="title"
                            label="TSL Learning Objective"
                            variant="outlined"
                            color="primary"
                            component={TextFinalField}
                            fullWidth
                            validate={required}
                            required
                            data-testid="title"
                        />
                        <Condition when="country" is={Country.GB}>
                            <Field
                                name="objective_type"
                                label="Objective Type"
                                variant="outlined"
                                color="primary"
                                component={SelectFinalField}
                                options={[
                                    { id: 'standard', name: 'Standard' },
                                    { id: 'sats', name: 'SATs' },
                                    { id: 'gcse', name: 'GCSEs' },
                                ]}
                                fullWidth
                                required
                                validate={required}
                            />
                            <Field
                                name="year_id"
                                component={SelectFinalField}
                                options={yearsOptions}
                                variant="outlined"
                                label="Year"
                                color="primary"
                                required
                                format={(value) =>
                                    value != null ? String(value) : ''
                                }
                                validate={required}
                            />
                        </Condition>
                        <Condition when="country" is={Country.US}>
                            <Field
                                name="standard_codes"
                                label="Standard code"
                                component={AutocompleteFinalField}
                                options={[]}
                                validate={requiredArray}
                                fullWidth
                                required
                                freeSolo
                                data-testid="standard_codes"
                            />
                            <Field
                                name="year_id"
                                component={SelectFinalField}
                                options={gradeOptions}
                                variant="outlined"
                                label="Grade"
                                color="primary"
                                required
                                format={(value) =>
                                    value != null ? String(value) : ''
                                }
                                validate={required}
                            />
                        </Condition>
                        <LearningContentsAttributes push={push} />
                        <Field
                            name="ai"
                            type="checkbox"
                            label="AI Learning Objective"
                            component={CheckboxFinalField}
                            data-testid="ai-chip-field"
                        />
                    </Box>
                    {learningObjective && (
                        <Box display="flex" justifyContent="center" my={2}>
                            <Button
                                onClick={changeActive}
                                color="secondary"
                                sx={sx.activateButton}
                                data-testid="activate-button"
                            >
                                {learningObjective.active
                                    ? 'Deactivate'
                                    : 'Activate'}
                            </Button>
                            {!learningObjective.active && (
                                <DeleteConfirmationDialog
                                    onDelete={handleDeleteLO}
                                    message="Are you sure you want to delete this learning objective?"
                                />
                            )}
                        </Box>
                    )}
                </>
            )}
        </>
    );
};

export default LearningObjectiveFormContent;
